import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { BrandLogo } from "../../components/Brand";
import { Plus, Search, X, Save, Upload, Star, Pencil } from "lucide-react";

const CATS = [
    "Apparel", "Jewellery", "Food & Beverage", "Travel", "Online Shopping",
    "Entertainment & Subscriptions", "Health & Wellness", "Electronics",
    "Groceries", "Accessories", "Hotels", "Home & Furniture",
];

export default function AdminBrands() {
    const [brands, setBrands] = useState([]);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | brand object
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (category) params.set("category", category);
        params.set("limit", "500");
        const r = await adminApi.get(`/api/admin/brands?${params}`);
        setBrands(r.data.brands);
    };
    useEffect(() => { load(); }, [category]);

    const filtered = useMemo(() => {
        if (!q) return brands;
        const t = q.toLowerCase();
        return brands.filter((b) => (b.name || "").toLowerCase().includes(t) || (b.category || "").toLowerCase().includes(t));
    }, [brands, q]);

    return (
        <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="font-display text-3xl font-bold">Brands ({brands.length})</h1>
                <button onClick={() => setEditing("new")} className="fx-btn-primary" data-testid="new-brand-btn">
                    <Plus size={14} /> Add brand
                </button>
            </div>

            <div className="flex gap-2 mt-4 items-center flex-wrap">
                <div className="flex items-center gap-2 fx-card px-3 py-2 flex-1 min-w-[220px]">
                    <Search size={16} className="text-[#6b7280]" />
                    <input data-testid="brand-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand / category" className="bg-transparent outline-none w-full text-sm" />
                </div>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="fx-input !w-auto">
                    <option value="">All categories</option>
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-5">
                {filtered.map((b) => (
                    <div key={b.id} className={`fx-card p-3 ${b.active === false ? "opacity-50" : ""}`}>
                        <div className="aspect-square rounded-xl overflow-hidden bg-[#fafafa] flex items-center justify-center mb-2">
                            <BrandLogo url={b.logo_url} fallback={b.fallback_logo_url} name={b.name} size={84} />
                        </div>
                        <p className="text-sm font-semibold truncate flex items-center gap-1">
                            {b.featured && <Star size={12} className="text-[#c9a959] fill-[#c9a959]" />}
                            {b.name}
                        </p>
                        <p className="text-[11px] text-[#6b7280] truncate">{b.category}</p>
                        <button onClick={() => setEditing(b)} className="mt-2 fx-btn-ghost w-full !py-1 !text-xs">
                            <Pencil size={11} /> Edit
                        </button>
                    </div>
                ))}
                {filtered.length === 0 && <p className="text-sm text-[#6b7280] col-span-full">No brands match.</p>}
            </div>

            {editing && (
                <BrandEditor
                    brand={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </div>
    );
}

function BrandEditor({ brand, onClose, onSaved }) {
    const isNew = !brand;
    const [form, setForm] = useState({
        name: brand?.name || "",
        category: brand?.category || "Apparel",
        domain: brand?.domain || "",
        description: brand?.description || "",
        featured: !!brand?.featured,
        active: brand?.active !== false,
        logo_url: brand?.logo_url || "",
        hero_image_url: brand?.hero_image_url || "",
    });
    const [busy, setBusy] = useState(false);

    const uploadImage = async (file, kind) => {
        if (!file) return null;
        const b64 = await new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result.split(",")[1]); r.readAsDataURL(file); });
        const r = await adminApi.post("/api/admin/upload-image", { file_base64: b64, mime_type: file.type, kind });
        return r.data.url;
    };

    const save = async () => {
        if (!form.name) return;
        setBusy(true);
        try {
            if (isNew) {
                await adminApi.post("/api/admin/brands", form);
            } else {
                await adminApi.patch(`/api/admin/brands/${brand.id}`, form);
            }
            onSaved();
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-xl font-bold">{isNew ? "Add brand" : `Edit · ${brand.name}`}</h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Brand name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fx-input" /></Field>
                    <Field label="Category *">
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="fx-input">
                            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Domain (for Clearbit logo)"><input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="lenskart.com" className="fx-input" /></Field>
                    <Field label="Description" wide><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fx-input" /></Field>

                    <ImgPicker
                        label="Brand logo (upload or paste URL)"
                        value={form.logo_url}
                        onUpload={async (f) => { const u = await uploadImage(f, "brand"); setForm({ ...form, logo_url: u }); }}
                        onUrl={(u) => setForm({ ...form, logo_url: u })}
                    />
                    <ImgPicker
                        label="Hero / banner image"
                        value={form.hero_image_url}
                        onUpload={async (f) => { const u = await uploadImage(f, "brand"); setForm({ ...form, hero_image_url: u }); }}
                        onUrl={(u) => setForm({ ...form, hero_image_url: u })}
                    />
                </div>

                <div className="mt-4 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
                    <button onClick={save} disabled={busy || !form.name} className="fx-btn-primary ml-auto" data-testid="save-brand-btn">
                        <Save size={14} /> {isNew ? "Create brand" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ImgPicker({ label, value, onUpload, onUrl }) {
    return (
        <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#6b7280]">{label}</label>
            <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-xl border border-[rgba(11,20,55,0.10)] bg-[#fafafa] flex items-center justify-center overflow-hidden">
                    {value ? <img src={value} alt="" className="w-full h-full object-contain" /> : <Upload size={20} className="text-[#9ca3af]" />}
                </div>
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} className="text-xs" />
                <input value={value || ""} onChange={(e) => onUrl(e.target.value)} placeholder="or paste URL" className="fx-input flex-1 min-w-[120px]" />
            </div>
        </div>
    );
}

function Field({ label, children, wide }) {
    return (
        <div className={wide ? "md:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-widest text-[#6b7280]">{label}</label>
            {children}
        </div>
    );
}

export { ImgPicker, Field };
